import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import DOMPurify from 'dompurify';

import { Block, Heading, Link, Typography } from '@klp/design';
import { DateTime } from '@klp/design/DateTime';
import { FileGallery, MimeTypes } from '@klp/file';

import { messageApiRoutes } from '../api';
import { MessageStatus } from '../classes';
import type { ByggKunde, Message } from '../classes';
import { MessageBoardRoutes } from '../config';

import { ByggKundeSelector } from './ByggKundeSelector';

interface IOwnProps {
    message: Message;
    isAdmin: boolean;
}

const useStyles = makeStyles((theme) => ({
    attachmentsContainer: {
        display: 'flex',
        gap: theme.spacing(2),
    },
    messageContent: {
        '& table': {
            borderCollapse: 'collapse',
            margin: 0,
            overflow: 'hidden',
            tableLayout: 'fixed',
            width: '100%',
        },
        '& td,th': {
            border: '1px solid lightgray',
            boxSizing: 'border-box',
            minWidth: '1em',
            padding: '6px 8px',
            position: 'relative',
            verticalAlign: 'top',

            '&> *': {
                marginBottom: 0,
            },
        },
        '& th': {
            backgroundColor: 'rgb(242, 242, 242)',
            fontWeight: 'bold',
            textAlign: 'left',
            verticalAlign: 'center',
        },
    },
}));

const RenderRecipients = (props: { list: Array<ByggKunde> }) => {
    const { t } = useTranslation();
    return (
        <ByggKundeSelector
            data-testid="MessageCardRecipients"
            value={props.list}
            name="mottakereList"
            primarySelect="kunde"
            secondarySelect="bygg"
            title={t('buildingClientChooser.recipients.select')}
            readonly={true}
        />
    );
};

export const MessageCard: React.FC<IOwnProps> = ({ message, isAdmin }) => {
    const { t } = useTranslation();
    const { Published, WillPublish } = MessageStatus;
    const classes = useStyles();

    if (!message) {
        return null;
    }

    const {
        id,
        title,
        content,
        status,
        createdAt,
        publishAt,
        createdByName,
        adminStatus,
        notificationSentAt,
    } = message;

    return (
        <>
            <Heading klpHeadingLevel="ONE" marginBottom="compact">
                {title}
            </Heading>
            {isAdmin && (
                <Typography data-testid="MessageCardMessageStatus">
                    {t('message.messageStatus')}
                    {': '}
                    {t(`message.status.${status}`)}
                </Typography>
            )}
            {[Published, WillPublish].includes(adminStatus) && (
                <Typography
                    marginVertical="compact"
                    data-testid="MessageCardAdminStatus"
                >
                    {t(`message.status.${adminStatus}`)}
                    {': '}
                    <DateTime date={publishAt ?? createdAt} /> {t('general.by')}{' '}
                    {createdByName}
                </Typography>
            )}
            {notificationSentAt && (
                <Alert severity="info" color="info">
                    {t('message.channel.notificationSent', {
                        timeSent: notificationSentAt?.toLocaleString(),
                    })}
                </Alert>
            )}
            <Typography
                marginVertical="compact"
                data-testid="MessageCardContent"
                className={classes.messageContent}
            >
                <span
                    dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(content),
                    }}
                />
            </Typography>

            <div className={classes.attachmentsContainer}>
                <FileGallery
                    data-testid="MessageCardImages"
                    source={message.attachments}
                    allowedMimeTypes={MimeTypes.image}
                    path={messageApiRoutes.messageFile(id)}
                    queueName="messageImages"
                    fileContentField="fileContent"
                />

                <FileGallery
                    data-testid="MessageCardAttachments"
                    source={message.attachments}
                    allowedMimeTypes={MimeTypes.document}
                    path={messageApiRoutes.messageFile(id)}
                    queueName="messageAttachments"
                    fileContentField="fileContent"
                />
            </div>

            {isAdmin && <RenderRecipients list={message.mottakereList} />}

            <Block marginVertical="fat">
                <Link
                    href={MessageBoardRoutes.list()}
                    translation="general.goBack"
                    klpTypographyVariant="small"
                    marginTop="compact"
                />
            </Block>
        </>
    );
};
